/**
 * MODULE: domain/sales-intelligence/frontend/components/InsightDisplay.tsx
 * PURPOSE: Display company research insights
 * 
 * DEPENDENCIES:
 *   - react: React library
 *   - ../../contracts: Type definitions
 *   - ./InsightDisplaySignalSections: Signal panels
 *   - ../utils/insightSignalNormalize: Defensive signal array parsing
 * 
 * PUBLIC API:
 *   - InsightDisplay: Component to render insights
 * 
 * SIDE EFFECTS:
 *   - None (pure presentation)
 * 
 * AGENT SAFETY: SAFE
 * FILE SIZE: 174 / 200 lines
 *
 * RECENT: 2026-04-08 – Split signal UI into InsightDisplaySignalSections (file-size guideline).
 * RECENT: 2026-04-08 – Dark mode (Tailwind dark:) for modal/shell parity; signal normalization in utils/insightSignalNormalize.ts.
 * RECENT: 2026-02-09 – Defensive display: normalize key_insights and source_urls to arrays (API/DB may return non-array); optional chaining for research_data; guard signals with typeof object. Fixes "insight.key_insights.map is not a function" when getInsight returns alternate shape.
 *
 * See: /README.md#documentation | /ARCHITECTURE.md | /domain/sales-intelligence/README.md
 */

import React from 'react';
import { CompanyInsight } from '../../contracts/sales-intelligence.types';
import {
  normalizeFundingSignals,
  normalizeHiringSignals,
  normalizeNewsSignals,
  normalizeTechSignals,
} from '../utils/insightSignalNormalize';
import { InsightDisplaySignalSections } from './InsightDisplaySignalSections';

interface InsightDisplayProps {
  insight: CompanyInsight;
}

/** Normalize key_insights to array (API/DB may return object or non-array). */
function normalizeKeyInsights(v: unknown): string[] {
  if (Array.isArray(v)) return v.filter((x): x is string => typeof x === 'string');
  if (v && typeof v === 'object' && !Array.isArray(v)) return [];
  return [];
}

/** Normalize source_urls to array. */
function normalizeSourceUrls(v: unknown): string[] {
  if (Array.isArray(v)) return v.filter((x): x is string => typeof x === 'string');
  return [];
}

function tierBadgeClass(tier?: string): string {
  switch (tier) {
    case 'hot':
      return 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200';
    case 'warm':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200';
    case 'cold':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
  }
}

function tierIcon(tier?: string): string {
  switch (tier) {
    case 'hot':
      return '🔥';
    case 'warm':
      return '☀️';
    case 'cold':
      return '❄️';
    default:
      return '📊';
  }
}

export const InsightDisplay: React.FC<InsightDisplayProps> = ({ insight }) => {
  const keyInsights = normalizeKeyInsights(insight?.key_insights);
  const sourceUrls = normalizeSourceUrls(insight?.source_urls);
  const userOverrides = insight?.user_overrides as Record<string, unknown> | undefined;
  const industry = insight.research_data?.industry?.trim() ?? '';
  const employeeRaw = insight.research_data?.employee_count ?? insight.research_data?.size;
  const employeeLabel =
    employeeRaw != null && String(employeeRaw).trim() !== ''
      ? `${String(employeeRaw).trim()} employees`
      : '';
  const firmographicSubtitle = [industry, employeeLabel].filter(Boolean).join(' · ');
  const rawSignals = insight?.signals && typeof insight.signals === 'object' ? insight.signals : null;
  const fundingList = normalizeFundingSignals(rawSignals?.funding);
  const hiringList = normalizeHiringSignals(rawSignals?.hiring);
  const techList = normalizeTechSignals(rawSignals?.tech_changes);
  const newsList = normalizeNewsSignals(rawSignals?.news);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">{insight.company_name}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 flex flex-wrap items-center gap-2">
            {firmographicSubtitle}
            {userOverrides && Object.prototype.hasOwnProperty.call(userOverrides, 'industry') && (
              <span
                className="text-xs font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
                title="You edited this field — it will not be overwritten by re-research"
              >
                Edited
              </span>
            )}
          </p>
        </div>

        <div className="flex items-center space-x-3">
          {insight.lead_score !== undefined && (
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">{insight.lead_score}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Score</div>
            </div>
          )}

          {insight.priority_tier && (
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${tierBadgeClass(insight.priority_tier)}`}>
              {tierIcon(insight.priority_tier)} {insight.priority_tier.toUpperCase()}
            </span>
          )}
        </div>
      </div>

      {insight.research_data?.summary && (
        <div className="bg-gray-50 dark:bg-gray-800/80 dark:border dark:border-gray-700 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Company Overview</h4>
          <p className="text-sm text-gray-600 dark:text-gray-300">{insight.research_data?.summary}</p>
        </div>
      )}

      {keyInsights.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">Key Insights</h4>
          <ul className="space-y-2">
            {keyInsights.map((item, index) => (
              <li key={index} className="flex items-start">
                <span className="text-blue-600 dark:text-blue-400 mr-2">•</span>
                <span className="text-sm text-gray-700 dark:text-gray-300">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <InsightDisplaySignalSections
        fundingList={fundingList}
        hiringList={hiringList}
        techList={techList}
        newsList={newsList}
      />

      {sourceUrls.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
            Sources ({sourceUrls.length})
          </h4>
          <div className="flex flex-wrap gap-2">
            {sourceUrls.slice(0, 3).map((url, index) => (
              <a
                key={index}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline"
              >
                Source {index + 1}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
